// src/pages/EmployerLeave.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DateTime } from "luxon";

import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  getDoc,
  getDocs,
  where,
  setDoc,
  writeBatch,
  deleteDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";

import { auth, db } from "../firebase/config";
import { FiSettings } from "react-icons/fi";
import {
  SlidersHorizontal,
  Tags,
  ShieldCheck,
  Scale,
  Info,
  Plus,
  UserRound,
  Clock3,
  Eye,
  Sparkles,
  CheckCircle2,
  Save,
  Trash2,
  EyeOff,
  ChevronRight,
} from "lucide-react";
import NoDataIllustration from "../tools/assets/undraw_searching-everywhere_tffi.svg";
import "../styles/EmployerLeave.css";

// ─────────────────────────────────────────────────────────────
// Helpers (move above the component)
const slugify = (s = "") =>
  s
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");

function getTypeCodeFromLeave(leave, leaveTypesAll) {
  // prefer code if present
  if (leave.leaveTypeCode) return leave.leaveTypeCode;

  const label = leave.leaveTypeLabel || leave.leaveType || "";
  const found = leaveTypesAll.find((t) => t.label === label);
  if (found?.code) return found.code;

  // fallback: slugify the label
  return slugify(label || "");
}

// UPDATED: respects Full Day vs Half Day + customStart/customEnd
function calcRequestedHours(leave, fullDayHours) {
  if (!leave?.startDate || !leave?.endDate) return 0;

  const start = DateTime.fromISO(leave.startDate);
  const end = DateTime.fromISO(leave.endDate);

  // count EVERY calendar day between start and end (inclusive)
  let days = 0;
  for (let d = start; d.toMillis() <= end.toMillis(); d = d.plus({ days: 1 })) {
    days++;
  }

  let hoursPerDay = 0;

  if (leave.timeOfDay === "Full Day") {
    hoursPerDay = Number(fullDayHours || 8);
  } else if (leave.timeOfDay === "Half Day") {
    const customStart = leave.customStart;
    const customEnd = leave.customEnd;

    if (customStart && customEnd) {
      const baseDate = "2000-01-01";
      const startDT = DateTime.fromISO(`${baseDate}T${customStart}`);
      const endDT = DateTime.fromISO(`${baseDate}T${customEnd}`);
      const diff = endDT.diff(startDT, "hours").hours;
      hoursPerDay = diff > 0 ? diff : 0;
    } else {
      hoursPerDay = 4;
    }
  } else {
    hoursPerDay = 4; // legacy
  }

  if (days === 0 || hoursPerDay === 0) return 0;
  return days * hoursPerDay;
}

const buildApproveConfirmMessage = (leave) => {
  const typeLabel = leave.leaveTypeLabel || leave.leaveType || "Leave";
  const empName = leave.employeeName || "this employee";

  return (
    `You are about to approve ${typeLabel} for ${empName}.\n\n` +
    `Once approved:\n` +
    `• A leave entry will appear in Time Tracking.\n` +
    `• The scheduled shift for those days will be blocked in Scheduling.\n` +
    `• The employee will be notified.\n\n` +
    `Do you want to continue?`
  );
};

function calcHoursPerDay(leave, fullDayHours) {
  if (!leave) return 0;

  if (leave.timeOfDay === "Full Day") {
    return Number(fullDayHours || 8);
  }

  if (leave.timeOfDay === "Half Day") {
    const customStart = leave.customStart;
    const customEnd = leave.customEnd;

    if (customStart && customEnd) {
      const baseDate = "2000-01-01";
      const startDT = DateTime.fromISO(`${baseDate}T${customStart}`);
      const endDT = DateTime.fromISO(`${baseDate}T${customEnd}`);
      const diff = endDT.diff(startDT, "hours").hours;
      return diff > 0 ? diff : 0;
    }

    return 4;
  }

  return 4;
}

export default function EmployerLeave() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [companyId, setCompanyId] = useState(null);

  const [employeesMap, setEmployeesMap] = useState({});
  const [employeesDataMap, setEmployeesDataMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authChecking, setAuthChecking] = useState(true);

  const [viewDocUrl, setViewDocUrl] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [showReasonModal, setShowReasonModal] = useState(false);
  const [fullReason, setFullReason] = useState("");

  const [selectedTab, setSelectedTab] = useState("Pending");

  const [timeOfDayFilter, setTimeOfDayFilter] = useState("All");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("All");
  const [employeeFilter, setEmployeeFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");

  // NEW: Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // 25 / 50 / 100

  // Seed test leave requests
  const [isSeedingLeave, setIsSeedingLeave] = useState(false);

  // Settings modal (Balances / Leave Types / Policy)
  const [showLeaveSettings, setShowLeaveSettings] = useState(false);

  // NEW: Filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Live leave types (enabled + all)
  const [leaveTypesAll, setLeaveTypesAll] = useState([]); // includes disabled
  const [leaveTypesEnabled, setLeaveTypesEnabled] = useState([]);

  // Balances tab state
  const [lmTab, setLmTab] = useState("types"); // "balances" | "types" | "policy"
  const [lmEmployeeId, setLmEmployeeId] = useState("");
  const [lmTypeCode, setLmTypeCode] = useState("");
  const [lmAmount, setLmAmount] = useState(8);
  const [lmMode, setLmMode] = useState("add"); // add | set | subtract

  const [companyTimeZone, setCompanyTimeZone] = useState("UTC"); // 🆕

  // 🆕 Leave policy: Full-day hours
  const [fullDayHours, setFullDayHours] = useState(8);
  const [savingPolicy, setSavingPolicy] = useState(false);

  // ✅ Custom confirm modal (replaces window.confirm)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmDanger, setConfirmDanger] = useState(false);
  const [onConfirm, setOnConfirm] = useState(null);
  const [hasAnyLeave, setHasAnyLeave] = useState(false);

  const openConfirm = ({
    title = "Confirm action",
    message = "Are you sure?",
    cta = "Confirm",
    danger = false,
    onYes,
  }) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmCta(cta);
    setConfirmDanger(danger);
    setOnConfirm(() => onYes); // store callback safely
    setShowConfirmModal(true);
  };

  const closeConfirm = () => {
    setShowConfirmModal(false);
    setOnConfirm(null);
  };

  // ─────────────────────────────────────────────────────────────
  // Auth → companyId
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("Employer not authenticated.");
        setAuthChecking(false);
        return;
      }

      try {
        const employerRef = doc(db, "users", user.uid);
        const employerDoc = await getDoc(employerRef);

        if (!employerDoc.exists()) throw new Error("Company not found.");

        const employerData = employerDoc.data();
        const cid = employerData.companyId;
        setCompanyId(cid);

        // 🆕 Pull company time zone + leave policy
        try {
          const companyRef = doc(db, "companies", cid);
          const companySnap = await getDoc(companyRef);
          if (companySnap.exists()) {
            const cData = companySnap.data();
            setCompanyTimeZone(cData?.timeZone || "UTC");

            // 🆕 leave policy full-day hours
            const policyHours = Number(cData?.leavePolicy?.fullDayHours);
            setFullDayHours(
              Number.isFinite(policyHours) && policyHours > 0 ? policyHours : 8,
            );
          }
        } catch (err) {
          console.warn("Failed to load company time zone / leave policy:", err);
        }

        // 🔹 Load saved leave filters if present for this company
        const savedFilters = employerData.leaveFilters;
        if (savedFilters && savedFilters.companyId === cid) {
          if (savedFilters.selectedTab)
            setSelectedTab(savedFilters.selectedTab);
          if (savedFilters.timeOfDayFilter)
            setTimeOfDayFilter(savedFilters.timeOfDayFilter);
          if (savedFilters.leaveTypeFilter)
            setLeaveTypeFilter(savedFilters.leaveTypeFilter);
          if (savedFilters.employeeFilter)
            setEmployeeFilter(savedFilters.employeeFilter);
          if (savedFilters.sortOrder) setSortOrder(savedFilters.sortOrder);
          if (savedFilters.pageSize) setPageSize(savedFilters.pageSize);
        }
      } catch (err) {
        setError(err.message || "Failed to resolve company.");
      } finally {
        setAuthChecking(false);
      }
    });

    return () => unsubscribe();
  }, []);
  useEffect(() => {
    if (!companyId) return;

    const base = collection(db, `companies/${companyId}/leave_requests`);

    // lightweight "any doc exists" check (live)
    const unsub = onSnapshot(
      query(base),
      (snap) => {
        setHasAnyLeave(!snap.empty);
      },
      () => {
        // if it errors, don’t block the UI
        setHasAnyLeave(false);
      },
    );

    return () => unsub();
  }, [companyId]);

  // ─────────────────────────────────────────────────────────────
  // 🆕 Save leave policy (Full-day hours)
  async function saveLeavePolicy() {
    if (!companyId) return;

    const n = Number(fullDayHours);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Full-day hours must be greater than 0.");
      return;
    }

    try {
      setSavingPolicy(true);
      await updateDoc(doc(db, "companies", companyId), {
        "leavePolicy.fullDayHours": n,
        "leavePolicy.updatedAt": serverTimestamp(),
      });
      toast.success("Leave policy updated.");
    } catch (e) {
      console.error("saveLeavePolicy error:", e);
      toast.error("Failed to save leave policy.");
    } finally {
      setSavingPolicy(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Live employees map (id → name)
  useEffect(() => {
    if (!companyId) return;

    const empRef = collection(db, `companies/${companyId}/employees`);
    const unsub = onSnapshot(
      empRef,
      (snap) => {
        const nameMap = {};
        const dataMap = {};

        snap.forEach((d) => {
          const data = d.data() || {};
          nameMap[d.id] = data?.name || "Unknown";
          dataMap[d.id] = data;
        });

        setEmployeesMap(nameMap);
        setEmployeesDataMap(dataMap);
      },
      (err) => {
        console.error("Employees live error:", err);
      },
    );

    return () => unsub();
  }, [companyId]);

  // Live leave types
  useEffect(() => {
    if (!companyId) return;

    const typesCol = collection(db, `companies/${companyId}/leave_types`);
    return onSnapshot(typesCol, (live) => {
      const rows = live.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLeaveTypesAll(rows);

      setLeaveTypesEnabled(
        rows
          .filter((t) => t.enabled !== false)
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    });
  }, [companyId]);

  // ─────────────────────────────────────────────────────────────
  // Live leave requests for the selected tab
  useEffect(() => {
    if (authChecking || !companyId) return;

    setLoading(true);

    const base = collection(db, `companies/${companyId}/leave_requests`);
    const qRef = query(base, where("status", "==", selectedTab));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLeaveRequests(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Failed to load leave requests (live).");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [authChecking, companyId, selectedTab]);

  // Format ISO date ("YYYY-MM-DD") -> "MM/DD/YYYY" in company TZ
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const dt = DateTime.fromISO(iso, { zone: companyTimeZone || "UTC" });
    return dt.isValid ? dt.toFormat("MM/dd/yyyy") : iso;
  };

  const leaveSettingsReady = useMemo(() => {
    const hasEnabledTypes =
      Array.isArray(leaveTypesEnabled) && leaveTypesEnabled.length > 0;

    const hasPolicy =
      Number.isFinite(Number(fullDayHours)) && Number(fullDayHours) > 0;

    return hasEnabledTypes && hasPolicy;
  }, [leaveTypesEnabled, fullDayHours]);

  // ─────────────────────────────────────────────────────────────
  // Derive display rows with employee names from the live map
  const displayRequests = useMemo(() => {
    return leaveRequests.map((r) => {
      const employeeName = employeesMap[r.employeeId] || "Unknown";
      const code = getTypeCodeFromLeave(r, leaveTypesAll);

      const emp = employeesDataMap[r.employeeId] || {};
      const currentBalance = Number(emp.timeBank?.[code] || 0);
      const toDeduct = calcRequestedHours(r, fullDayHours);

      return {
        ...r,
        employeeName,
        leaveTypeCode: code,
        availableHours: currentBalance,
        toDeduct,
      };
    });
  }, [
    leaveRequests,
    employeesMap,
    employeesDataMap,
    leaveTypesAll,
    fullDayHours,
  ]);

  const timeOfDayOptions = useMemo(() => {
    return Array.from(
      new Set(displayRequests.map((r) => r.timeOfDay).filter(Boolean)),
    );
  }, [displayRequests]);

  // Filters + sort
  const filteredSortedRequests = useMemo(() => {
    const filtered = displayRequests
      .filter((leave) =>
        leaveTypeFilter === "All"
          ? true
          : leave.leaveTypeLabel === leaveTypeFilter,
      )
      .filter((leave) =>
        employeeFilter === "All" ? true : leave.employeeName === employeeFilter,
      )
      .filter((leave) =>
        timeOfDayFilter === "All"
          ? true
          : (leave.timeOfDay || "") === timeOfDayFilter,
      );

    return filtered.sort((a, b) => {
      const aDate = new Date(a.startDate);
      const bDate = new Date(b.startDate);
      return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
    });
  }, [
    displayRequests,
    leaveTypeFilter,
    employeeFilter,
    timeOfDayFilter,
    sortOrder,
  ]);

  // NEW: pagination derived from filteredSortedRequests
  const totalRequests = filteredSortedRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalRequests / pageSize));

  // Reset page when filters / tab / pageSize change
  useEffect(() => {
    setPage(1);
  }, [
    leaveTypeFilter,
    employeeFilter,
    timeOfDayFilter,
    sortOrder,
    selectedTab,
    pageSize,
  ]);

  // Clamp page when totalPages shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (page - 1) * pageSize;

  const pagedRequests = useMemo(() => {
    return filteredSortedRequests.slice(startIndex, startIndex + pageSize);
  }, [filteredSortedRequests, startIndex, pageSize]);

  const handleApplyFilters = async () => {
    // Close the modal immediately for better UX
    setShowFilterModal(false);

    const user = auth.currentUser;
    if (!user || !companyId) return;

    const payload = {
      companyId,
      selectedTab,
      timeOfDayFilter,
      leaveTypeFilter,
      employeeFilter,
      sortOrder,
      pageSize,
    };

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { leaveFilters: payload }, { merge: true });
    } catch (e) {
      console.error("Failed to save leave filters", e);
    }
  };

  // helper to clear filters from modal + Firestore
  const resetFilters = async () => {
    setEmployeeFilter("All");
    setLeaveTypeFilter("All");
    setTimeOfDayFilter("All");
    setSortOrder("asc");
    setSelectedTab("Pending");
    setPageSize(25);
    setPage(1);

    setShowFilterModal(false);

    const user = auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { leaveFilters: deleteField() }, { merge: true });
    } catch (e) {
      console.error("Failed to clear leave filters", e);
    }
  };

  const pushEmployeeNotification = async ({
    employeeId,
    title,
    message,
    type,
    leaveId,
    status,
  }) => {
    if (!employeeId) return;

    // ✅ deterministic id so it won’t duplicate if you click twice or refresh
    const notifId = `leaveStatus_${leaveId}_${status}`;

    await setDoc(
      doc(db, `users/${employeeId}/notifications`, notifId),
      {
        archived: false,
        read: false,

        companyId,
        employeeId,

        type: type || "leaveStatus", // e.g. "leaveApproved" | "leaveRejected"
        title,
        message,

        leaveId,
        status, // "Approved" | "Rejected" | etc.

        createdAt: serverTimestamp(), // ✅ works with your bell orderBy("createdAt")
      },
      { merge: true },
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Approve / Reject / Cancel Approved (with date protection)
  const handleLeaveAction = async (leave, action) => {
    try {
      if (!companyId) return;

      const leaveRef = doc(
        db,
        `companies/${companyId}/leave_requests`,
        leave.id,
      );

      const prevStatus = leave.status; // "Pending" | "Approved" | "Rejected"
      const newStatus = action;

      // 🛡️ PROTECTION: Do NOT allow cancelling once leave has started or passed
      if (prevStatus === "Approved" && newStatus !== "Approved") {
        // Use company TZ for "today"
        const now = DateTime.now().setZone(companyTimeZone || "UTC");
        const todayISO = now.toISODate(); // "YYYY-MM-DD"
        const startISO = leave.startDate;
        const endISO = leave.endDate || leave.startDate;

        if (todayISO >= startISO && todayISO <= endISO) {
          toast.error(
            "You can’t cancel this leave because it has already started.",
          );
          return;
        }

        if (todayISO > endISO) {
          toast.error(
            "You can’t cancel this leave because the leave dates have already passed.",
          );
          return;
        }
      }

      // 1) Update main leave request status
      await updateDoc(leaveRef, { status: newStatus });

      // ✅ EMPLOYEE NOTIFICATION (shows in EmployeeDashboard bell)
      const start = leave.startDate;
      const end = leave.endDate || leave.startDate;
      const typeLabel = leave.leaveTypeLabel || leave.leaveType || "Leave";

      let notifTitle = "Leave request updated";
      let notifType = "leaveStatus";
      let notifMsg = `${typeLabel} (${start} → ${end}) is now ${newStatus}.`;

      if (newStatus === "Approved") {
        notifTitle = "Leave approved";
        notifType = "leaveApproved";
        notifMsg = `Your ${typeLabel} request (${start} → ${end}) was approved.`;
      }

      if (newStatus === "Rejected") {
        notifTitle = "Leave rejected";
        notifType = "leaveRejected";
        notifMsg = `Your ${typeLabel} request (${start} → ${end}) was rejected.`;
      }
      if (newStatus === "Cancelled") {
        notifTitle = "Leave cancelled";
        notifType = "leaveCancelled";
        notifMsg = `Your approved ${typeLabel} leave (${start} → ${end}) was cancelled by your employer.`;
      }

      await pushEmployeeNotification({
        employeeId: leave.employeeId,
        title: notifTitle,
        message: notifMsg,
        type: notifType,
        leaveId: leave.id,
        status: newStatus,
      });

      // 2) TIME BANK adjust (both directions)
      const employeeRef = doc(
        db,
        `companies/${companyId}/employees`,
        leave.employeeId,
      );

      const empSnap = await getDoc(employeeRef);
      const employee = empSnap.exists() ? empSnap.data() : null;

      if (employee?.timeBankEnabled) {
        // Resolve the leave type code once
        let code = leave.leaveTypeCode;

        if (!code) {
          const found = leaveTypesAll.find(
            (t) =>
              t.label === leave.leaveType || t.label === leave.leaveTypeLabel,
          );
          code =
            found?.code ||
            (leave.leaveType || leave.leaveTypeLabel || "")
              .toString()
              .trim()
              .toUpperCase()
              .replace(/[^\w]+/g, "_");
        }

        const totalHours = calcRequestedHours(leave, fullDayHours);

        const currentBalance = Number(employee.timeBank?.[code] || 0);
        let newBalance = currentBalance;

        // ✅ First time approving → deduct
        if (prevStatus !== "Approved" && newStatus === "Approved") {
          newBalance = Math.max(currentBalance - totalHours, 0);
        }

        // ✅ Cancelling an already-approved leave → refund
        if (prevStatus === "Approved" && newStatus !== "Approved") {
          newBalance = currentBalance + totalHours;
        }

        if (newBalance !== currentBalance) {
          await updateDoc(employeeRef, { [`timeBank.${code}`]: newBalance });
        }
      }

      // 3) TIME TRACKING sync (only when crossing the "Approved" boundary)
      if (prevStatus !== "Approved" && newStatus === "Approved") {
        // Just approved now → create synthetic paid-leave entries
        await syncLeaveToTimeTracking(companyId, leave);
      } else if (prevStatus === "Approved" && newStatus !== "Approved") {
        // Was approved, now being cancelled/rejected → remove synthetic entries
        await deleteLeaveTimeEntries(companyId, leave.id);
      }

      // 4) Leave history (audit)
      const historyRef = doc(
        collection(db, `companies/${companyId}/leave_history`),
        leave.id,
      );

      await setDoc(
        historyRef,
        {
          ...leave,
          status: newStatus,
          decisionDate: new Date().toISOString(),
        },
        { merge: true },
      );

      toast.success(`Leave ${newStatus.toLowerCase()} successfully.`);
    } catch (err) {
      console.error("❌ Error in handleLeaveAction:", err);
      toast.error("Failed to update leave status.");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Time Bank / Leave Types helpers

  function EmployeeBalancesPreview({ companyId, employeeId, leaveTypes }) {
    const [emp, setEmp] = useState(null);

    useEffect(() => {
      if (!companyId || !employeeId) {
        setEmp(null);
        return;
      }

      const ref = doc(db, `companies/${companyId}/employees`, employeeId);
      return onSnapshot(ref, (snap) =>
        setEmp(snap.exists() ? snap.data() : null),
      );
    }, [companyId, employeeId]);

    if (!employeeId) return null;

    const bank = emp?.timeBank || {};

    return (
      <div className="lm-balances">
        <h4>Balances</h4>
        <ul>
          {Object.keys(bank).length === 0 ? (
            <li className="lm-muted">No balances yet.</li>
          ) : (
            Object.entries(bank)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([code, hrs]) => {
                const t = leaveTypes.find((x) => x.code === code);
                return (
                  <li key={code}>
                    <span
                      className="lm-dot"
                      style={{ background: t?.color || "#94a3b8" }}
                    />
                    <span>{t?.label || code.replace(/_/g, " ")}</span>
                    <strong>{Number(hrs || 0)}h</strong>
                    {t?.enabled === false && (
                      <em className="lm-muted"> (disabled)</em>
                    )}
                  </li>
                );
              })
          )}
        </ul>
      </div>
    );
  }

  function LeaveTypesEditor({ companyId, leaveTypesAll }) {
    const [label, setLabel] = useState("");
    const [color, setColor] = useState("#3d8bfd");

    const addLeaveType = async () => {
      const code = slugify(label);
      if (!code) return;

      await setDoc(doc(db, `companies/${companyId}/leave_types`, code), {
        label: label.trim(),
        code,
        color,
        enabled: true,
        createdAt: DateTime.now().toISO(),
      });

      setLabel("");
    };

    const updateLeaveType = async (code, patch) => {
      await updateDoc(
        doc(db, `companies/${companyId}/leave_types`, code),
        patch,
      );
    };

    const disableType = async (code) => {
      await updateLeaveType(code, { enabled: false });
    };

    const enableType = async (code) => {
      await updateLeaveType(code, { enabled: true });
    };

    async function deleteLeaveTypeStrict(t) {
      const reqCol = collection(db, `companies/${companyId}/leave_requests`);
      const reqSnap = await getDocs(
        query(reqCol, where("leaveType", "==", t.label)),
      );

      if (!reqSnap.empty) {
        alert(
          `Cannot delete "${t.label}" because there are existing leave requests using it.\nDisable it instead to preserve history.`,
        );
        return;
      }

      const empCol = collection(db, `companies/${companyId}/employees`);
      const empSnap = await getDocs(empCol);

      const batch = writeBatch(db);

      empSnap.forEach((d) => {
        const tb = d.data().timeBank || {};
        if (tb[t.code] != null) {
          batch.update(doc(empCol, d.id), {
            [`timeBank.${t.code}`]: deleteField(),
          });
        }
      });

      await batch.commit();
      await deleteDoc(doc(db, `companies/${companyId}/leave_types`, t.code));
    }

    return (
      <div className="lm-types-content">
        {/* Add new leave type */}
        <section className="lm-section-card">
          <div className="lm-section-heading">
            <h4>Add New Leave Type</h4>
            <p>Create a new category that employees can request.</p>
          </div>

          <div className="lm-add-type-grid">
            <label className="lm-field lm-type-name-field">
              <span>Type Label</span>

              <div className="lm-input-with-icon">
                <Tags size={18} />

                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Bereavement, Comp Off"
                />
              </div>
            </label>

            <label className="lm-field lm-color-field">
              <span>Color</span>

              <div className="lm-color-picker-wrap">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />

                <span>{color.toUpperCase()}</span>
              </div>
            </label>

            <div className="lm-add-type-action">
              <button
                type="button"
                className="lm-primary lm-add-type-btn"
                onClick={addLeaveType}
                disabled={!label.trim()}
              >
                <Plus size={18} />
                Add Type
              </button>
            </div>
          </div>
        </section>

        {/* Configured Leave Types */}
        <section className="lm-types-section">
          <div className="lm-section-heading">
            <h4>Configured Leave Types</h4>
            <p>
              Manage your existing leave categories. Disable a type to hide it
              from selection while keeping history and balances.
            </p>
          </div>

          <div className="lm-types-table">
            <div className="lm-types-table-header">
              <div>Type Name</div>
              <div>Color</div>
              <div>Status</div>
              <div>Actions</div>
            </div>

            {[...leaveTypesAll]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((t) => (
                <div className="lm-types-table-row" key={t.code}>
                  <div className="lm-type-name-cell">
                    <span
                      className="lm-type-dot"
                      style={{ background: t.color || "#94a3b8" }}
                    />

                    <input
                      className="lm-type-name-input"
                      value={t.label}
                      onChange={(e) =>
                        updateLeaveType(t.code, {
                          label: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <div className="lm-table-color-picker">
                      <input
                        type="color"
                        value={t.color || "#94a3b8"}
                        onChange={(e) =>
                          updateLeaveType(t.code, {
                            color: e.target.value,
                          })
                        }
                      />

                      <span>{(t.color || "#94a3b8").toUpperCase()}</span>
                    </div>
                  </div>

                  <div>
                    <span
                      className={`lm-status-pill ${
                        t.enabled === false ? "disabled" : "enabled"
                      }`}
                    >
                      <span className="lm-status-dot" />
                      {t.enabled === false ? "Disabled" : "Enabled"}
                    </span>
                  </div>

                  <div className="lm-type-actions">
                    {t.enabled === false ? (
                      <button
                        type="button"
                        className="lm-enable-btn"
                        onClick={() => enableType(t.code)}
                      >
                        <CheckCircle2 size={16} />
                        Enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="lm-disable-btn"
                        onClick={() => disableType(t.code)}
                      >
                        <EyeOff size={16} />
                        Disable
                      </button>
                    )}

                    <button
                      type="button"
                      className="lm-delete-btn"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${t.label}" permanently? This cannot be undone.`,
                          )
                        ) {
                          deleteLeaveTypeStrict(t).catch((e) => {
                            console.error(e);
                            alert(
                              "Failed to delete type. See console for details.",
                            );
                          });
                        }
                      }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}

            {leaveTypesAll.length === 0 && (
              <div className="lm-types-empty">
                No leave types have been created yet.
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  // apply button handler in modal
  async function grantHoursFromModal() {
    if (!companyId || !lmEmployeeId || !lmTypeCode) return;

    const empRef = doc(db, `companies/${companyId}/employees`, lmEmployeeId);
    const snap = await getDoc(empRef);

    const emp = snap.exists() ? snap.data() : null;
    if (!emp) return;

    const curr = Number(emp.timeBank?.[lmTypeCode] || 0);
    const amt = Number(lmAmount || 0);

    let next = curr;
    if (lmMode === "add") next = curr + amt;
    if (lmMode === "subtract") next = Math.max(0, curr - amt);
    if (lmMode === "set") next = Math.max(0, amt);

    await updateDoc(empRef, {
      timeBankEnabled: true,
      [`timeBank.${lmTypeCode}`]: next,
    });
  }

  // NEW: remove any existing auto leave time entries for this leave (idempotent)
  async function deleteLeaveTimeEntries(companyId, leaveId) {
    if (!companyId || !leaveId) return;

    const timeCol = collection(db, `companies/${companyId}/time_tracking`);
    const qRef = query(timeCol, where("leaveId", "==", leaveId));
    const snap = await getDocs(qRef);

    if (snap.empty) return;

    const batch = writeBatch(db);
    snap.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  async function syncLeaveToTimeTracking(companyId, leave) {
    if (
      !companyId ||
      !leave?.employeeId ||
      !leave.startDate ||
      !leave.endDate
    ) {
      return;
    }

    // ✅ Use company leave policy for Full Day
    const hoursPerDay = calcHoursPerDay(leave, fullDayHours);
    if (!hoursPerDay || hoursPerDay <= 0) return;

    const timeCol = collection(db, `companies/${companyId}/time_tracking`);
    const start = DateTime.fromISO(leave.startDate);
    const end = DateTime.fromISO(leave.endDate);

    await deleteLeaveTimeEntries(companyId, leave.id);

    const typeCode = getTypeCodeFromLeave(leave, leaveTypesAll);

    for (
      let d = start;
      d.toMillis() <= end.toMillis();
      d = d.plus({ days: 1 })
    ) {
      const dateStr = d.toISODate();
      const docId = `${leave.employeeId}_${dateStr}_leave_${leave.id}`;

      let clockIn = "09:00";
      let clockOut = "17:00";

      // ✅ Full Day clockOut matches policy hours
      if (leave.timeOfDay === "Full Day") {
        const baseDate = "2000-01-01";
        const startDT = DateTime.fromISO(`${baseDate}T${clockIn}`);
        const endDT = startDT.plus({ minutes: Math.round(hoursPerDay * 60) });
        clockOut = endDT.toFormat("HH:mm");
      }

      if (
        leave.timeOfDay === "Half Day" &&
        leave.customStart &&
        leave.customEnd
      ) {
        clockIn = leave.customStart;
        clockOut = leave.customEnd;
      }

      await setDoc(doc(timeCol, docId), {
        userId: leave.employeeId,
        date: dateStr,
        clockIn,
        clockOut,

        // ✅ THIS is what your EmployerTimeTracking table displays
        hoursWorked: hoursPerDay,

        status: "On Leave",
        entryType: "paid-leave",
        leaveId: leave.id,
        leaveTypeCode: typeCode,
        leaveTypeLabel: leave.leaveTypeLabel || leave.leaveType || null,
        createdFromLeave: true,
        createdAt: new Date().toISOString(),
      });
    }
  }

  // Seed fake leave requests for this company (load testing)
  const handleSeedLeave = async () => {
    if (!companyId) {
      toast.error("Company not resolved yet.");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast.error("You must be logged in as an employer to seed data.");
      return;
    }

    try {
      setIsSeedingLeave(true);

      const token = await user.getIdToken();
      const url =
        "https://us-central1-employee-b47f3.cloudfunctions.net/seedTestLeaveRequests";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId,
          daysBack: 60,
          maxPerDay: 5,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("seedTestLeaveRequests error:", data);
        toast.error(data.error || "Failed to seed leave requests.");
        return;
      }

      toast.success(
        `Seeded ${data.created || 0} test leave requests for this company.`,
      );
    } catch (err) {
      console.error("handleSeedLeave error:", err);
      toast.error("Unexpected error seeding leave requests.");
    } finally {
      setIsSeedingLeave(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // UI
  return (
    <div className="leave-title-container ">
      <div className="leave-wrapper">
        <ToastContainer />

        {/* Document viewer modal */}
        {showModal && (
          <div className="leave-modal-overlay">
            <div className="leave-modal-content">
              <button
                className="leave-modal-close"
                onClick={() => setShowModal(false)}
              >
                ✖
              </button>

              <iframe
                src={viewDocUrl}
                width="100%"
                height="500px"
                title="Document Viewer"
              />
            </div>
          </div>
        )}

        {/* Full reason modal */}
        {showReasonModal && (
          <div className="reason-modal-overlay">
            <div className="reason-modal-content">
              <button
                className="reason-modal-close"
                onClick={() => setShowReasonModal(false)}
              >
                ✖
              </button>
              <h3>Full Leave Reason</h3>
              <p>{fullReason}</p>
            </div>
          </div>
        )}

        {/* Filter modal (Employee / Leave Type / Time of Day / Start Date sort) */}
        {showFilterModal && (
          <div
            className="leave-filter-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowFilterModal(false);
            }}
          >
            <div className="leave-filter-modal">
              <div className="leave-filter-header">
                <h3>Filter &amp; Sort</h3>
                <button
                  className="leave-filter-close"
                  onClick={() => setShowFilterModal(false)}
                >
                  ✖
                </button>
              </div>

              <div className="leave-filter-grid">
                <div className="leave-filter-field">
                  <span>Employee</span>
                  <select
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                  >
                    <option value="All">All</option>
                    {Array.from(
                      new Set(displayRequests.map((r) => r.employeeName)),
                    ).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="leave-filter-field">
                  <span>Leave Type</span>
                  <select
                    value={leaveTypeFilter}
                    onChange={(e) => setLeaveTypeFilter(e.target.value)}
                  >
                    <option value="All">All</option>
                    {leaveTypesEnabled.map((t) => (
                      <option key={t.code} value={t.label}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="leave-filter-field">
                  <span>Time of Day</span>
                  <select
                    value={timeOfDayFilter}
                    onChange={(e) => setTimeOfDayFilter(e.target.value)}
                  >
                    <option value="All">All</option>
                    {timeOfDayOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="leave-filter-field">
                  <span>Sort by Start Date</span>
                  <div className="leave-filter-sort-buttons">
                    <button
                      type="button"
                      onClick={() => setSortOrder("asc")}
                      className={
                        sortOrder === "asc" ? "active-sort" : undefined
                      }
                    >
                      Ascending
                    </button>
                    <button
                      type="button"
                      onClick={() => setSortOrder("desc")}
                      className={
                        sortOrder === "desc" ? "active-sort" : undefined
                      }
                    >
                      Descending
                    </button>
                  </div>
                </div>
              </div>

              <div className="leave-filter-actions">
                <button
                  type="button"
                  className="leave-filter-reset"
                  onClick={resetFilters}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="leave-filter-apply"
                  onClick={handleApplyFilters}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="leave-tabs">
          <div className="leave-tabs-left">
            {["Pending", "Approved", "Rejected", "Cancelled"].map((tab) => (
              <button
                key={tab}
                className={`leave-tab ${tab === selectedTab ? "active" : ""}`}
                onClick={() => setSelectedTab(tab)}
              >
                {tab}
              </button>
            ))}

            {false && (
              <button
                className="leave-seed-btn"
                onClick={handleSeedLeave}
                disabled={!companyId || isSeedingLeave}
                title="Seed test leave requests for this company"
              >
                {isSeedingLeave ? "Seeding..." : "Seed Test Leave"}
              </button>
            )}
          </div>

          <div className="leave-tabs-right">
            <button
              className="leave-settings-btn"
              onClick={() => setShowLeaveSettings(true)}
              title="Leave settings"
              aria-label="Leave settings"
            >
              <FiSettings size={18} />
            </button>

            <button
              className="leave-filter-btn"
              onClick={() => setShowFilterModal(true)}
              title="Filter & sort leave requests"
            >
              <SlidersHorizontal size={16} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Table */}
        {authChecking || loading ? (
          <p className="loading-text">Loading leave requests...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : filteredSortedRequests.length === 0 ? (
          !hasAnyLeave ? (
            // ✅ TRUE EMPTY (no leave requests exist yet)
            <div className="leave-empty">
              <img
                src={NoDataIllustration}
                alt="No leave requests yet"
                className="leave-empty__img"
              />

              <h3 className="leave-empty__title">No leave requests yet</h3>

              <p className="leave-empty__text">
                Once employees submit time off, you’ll review and approve
                requests here.
              </p>

              <div className="leave-empty__actions">
                <button
                  type="button"
                  className="leave-empty__btn primary"
                  onClick={() => setShowLeaveSettings(true)}
                >
                  Setup leave settings
                </button>

                <button
                  type="button"
                  className="leave-empty__btn"
                  onClick={() => setShowFilterModal(true)}
                >
                  Open filters
                </button>
              </div>

              <div className="leave-empty__steps">
                <div
                  className={`leave-empty__step ${
                    leaveSettingsReady ? "done" : ""
                  }`}
                >
                  <span
                    className={`leave-empty__chip ${
                      leaveSettingsReady ? "done" : ""
                    }`}
                  >
                    {leaveSettingsReady ? "✓" : "1"}
                  </span>
                  {leaveSettingsReady
                    ? "Leave settings configured"
                    : "Set up leave settings"}
                </div>
                <div className="leave-empty__step">
                  <span className="leave-empty__chip">2</span>
                  Wait for employees to submit requests
                </div>
                <div className="leave-empty__step">
                  <span className="leave-empty__chip">3</span>
                  Start reviewing &amp; approving requests
                </div>
              </div>
              <div className="ex-empty__hint">
                Tip: Configure your leave types and policy settings first so
                employees can submit leave request using the correct types and
                policy
              </div>
            </div>
          ) : (
            // ✅ FILTERED EMPTY (data exists, but tab/filters show none)
            <div className="leave-noresults">
              <div className="leave-noresults__title">
                No matching leave requests
              </div>
              <div className="leave-noresults__text">
                Try another tab or adjust your filters.
              </div>
            </div>
          )
        ) : (
          <div className="leave-card">
            <div className="leave-table-scroll">
              <table className="leave-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Leave Type</th>
                    <th>Time of Day</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Avail. Hours</th>
                    <th>To Deduct</th>
                    <th>Reason</th>
                    <th>Document</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRequests.map((leave) => (
                    <tr key={leave.id}>
                      <td>{leave.employeeName}</td>
                      <td>{leave.leaveTypeLabel}</td>

                      <td>
                        {leave.timeOfDay || "—"}
                        {leave.timeOfDay === "Half Day" &&
                          leave.customStart &&
                          leave.customEnd && (
                            <div className="time-subtext">
                              {leave.customStart}–{leave.customEnd}
                            </div>
                          )}
                      </td>

                      <td>{fmtDate(leave.startDate)}</td>
                      <td>{fmtDate(leave.endDate)}</td>

                      <td>
                        <span
                          className={
                            leave.availableHours === 0
                              ? "lm-badge off"
                              : undefined
                          }
                          title={`Balance for ${leave.leaveTypeLabel}`}
                        >
                          {leave.availableHours}h
                        </span>
                      </td>

                      <td>
                        <span
                          className={
                            leave.toDeduct > leave.availableHours
                              ? "lm-badge off"
                              : undefined
                          }
                          title="Hours requested for this leave"
                        >
                          {leave.toDeduct}h
                        </span>
                      </td>

                      <td
                        className="reason-cell"
                        title="Click to view full reason"
                        onClick={() => {
                          setFullReason(leave.reason);
                          setShowReasonModal(true);
                        }}
                      >
                        {leave.reason}
                      </td>

                      <td>
                        {leave.supportingDocUrl ? (
                          <button
                            className="doc-link"
                            onClick={() => {
                              setViewDocUrl(leave.supportingDocUrl);
                              setShowModal(true);
                            }}
                          >
                            📎 View
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td>
                        {leave.status === "Pending" ? (
                          <div className="leave-action-buttons">
                            <button
                              className="leave-btn-approve"
                              onClick={() => {
                                openConfirm({
                                  title: "Approve this leave request?",
                                  message: buildApproveConfirmMessage(leave),
                                  cta: "Yes, approve leave",
                                  danger: false,
                                  onYes: () =>
                                    handleLeaveAction(leave, "Approved"),
                                });
                              }}
                              disabled={
                                employeesDataMap[leave.employeeId]
                                  ?.timeBankEnabled &&
                                leave.toDeduct > leave.availableHours
                              }
                              title={
                                employeesDataMap[leave.employeeId]
                                  ?.timeBankEnabled &&
                                leave.toDeduct > leave.availableHours
                                  ? "Insufficient hours"
                                  : ""
                              }
                            >
                              Approve
                            </button>

                            <button
                              className="leave-btn-reject"
                              onClick={() =>
                                handleLeaveAction(leave, "Rejected")
                              }
                            >
                              Reject
                            </button>
                          </div>
                        ) : leave.status === "Approved" ? (
                          // ✅ APPROVED TAB: show cancel mechanism
                          <div className="leave-action-buttons">
                            <span className="leave-status-badge approved">
                              Approved
                            </span>
                            <button
                              className="leave-btn-reject"
                              onClick={() => {
                                openConfirm({
                                  title: "Cancel approved leave?",
                                  message:
                                    "This will refund hours and remove paid-leave time entries. Do you want to continue?",
                                  cta: "Yes, cancel leave",
                                  danger: true,
                                  onYes: () =>
                                    handleLeaveAction(leave, "Cancelled"),
                                });
                              }}
                            >
                              Cancel Leave
                            </button>
                          </div>
                        ) : leave.status === "Cancelled" ? (
                          <span className="leave-status-badge cancelled">
                            Cancelled
                          </span>
                        ) : (
                          <span className="leave-status-badge rejected">
                            Rejected
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalRequests > pageSize && (
              <div className="leave-pagination-bar">
                <div className="leave-pagination-info">
                  Showing {startIndex + 1}–
                  {Math.min(startIndex + pageSize, totalRequests)} of{" "}
                  {totalRequests}
                </div>

                <div className="leave-pagination-controls">
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    ⟪ First
                  </button>

                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    ‹ Prev
                  </button>

                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next ›
                  </button>

                  <button
                    type="button"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                  >
                    Last ⟫
                  </button>

                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value) || 25);
                      setPage(1);
                    }}
                  >
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Leave Settings modal (Balances / Types / Policy) */}
      {/* Leave Settings modal (Balances / Types / Policy) */}
      {showLeaveSettings && (
        <div
          className="lm-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLeaveSettings(false);
          }}
        >
          <div className="lm-modal">
            <div className="lm-settings-header">
              <div className="lm-settings-heading">
                <div className="lm-settings-icon">
                  {lmTab === "types" && <Tags size={24} />}
                  {lmTab === "policy" && <ShieldCheck size={24} />}
                  {lmTab === "balances" && <Scale size={24} />}
                </div>

                <div>
                  <h3>Leave Management Settings</h3>

                  <div className="lm-setup-order">
                    <button
                      type="button"
                      className={lmTab === "types" ? "current" : ""}
                      onClick={() => setLmTab("types")}
                    >
                      Leave Types
                    </button>
                    <ChevronRight size={16} />
                    <button
                      type="button"
                      className={lmTab === "policy" ? "current" : ""}
                      onClick={() => setLmTab("policy")}
                    >
                      Policy
                    </button>
                    <ChevronRight size={16} />
                    <button
                      type="button"
                      className={lmTab === "balances" ? "current" : ""}
                      onClick={() => setLmTab("balances")}
                    >
                      Balances
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="lm-close"
                onClick={() => setShowLeaveSettings(false)}
                aria-label="Close settings"
              >
                ×
              </button>
            </div>

            <div className="lm-tabs">
              <button
                type="button"
                className={lmTab === "types" ? "active" : ""}
                onClick={() => setLmTab("types")}
              >
                <Tags size={18} />
                <span>Leave Types</span>
              </button>

              <button
                type="button"
                className={lmTab === "policy" ? "active" : ""}
                onClick={() => setLmTab("policy")}
              >
                <ShieldCheck size={18} />
                <span>Policy</span>
              </button>

              <button
                type="button"
                className={lmTab === "balances" ? "active" : ""}
                onClick={() => setLmTab("balances")}
              >
                <Scale size={18} />
                <span>Balances</span>
              </button>
            </div>

            {lmTab === "balances" ? (
              <div className="lm-body">
                {/* Balance information banner */}
                <div className="lm-balance-info-banner">
                  <Info size={22} />

                  <div>
                    <p>Manage employee time bank balances.</p>

                    <p>
                      When a leave request is <strong>approved</strong>, hours
                      can be deducted automatically from this balance.
                    </p>
                  </div>
                </div>

                {/* Setup warning */}
                {leaveTypesEnabled.length === 0 && (
                  <div className="lm-balance-warning">
                    <Info size={19} />

                    <div>
                      <strong>Create a Leave Type first.</strong>
                      <span>
                        You need at least one enabled leave type before
                        assigning employee balances.
                      </span>
                    </div>

                    <button type="button" onClick={() => setLmTab("types")}>
                      Leave Types
                    </button>
                  </div>
                )}

                {/* Main balance card */}
                <section className="lm-balance-card">
                  <div className="lm-balance-card-heading">
                    <h4>Update Employee Balance</h4>
                    <p>
                      Add, deduct, or set an employee&apos;s available leave
                      hours.
                    </p>
                  </div>

                  <div className="lm-balance-form-grid">
                    {/* Employee */}
                    <label className="lm-balance-field">
                      <span>Employee</span>

                      <div className="lm-balance-control">
                        <UserRound size={19} />

                        <select
                          value={lmEmployeeId}
                          onChange={(e) => setLmEmployeeId(e.target.value)}
                        >
                          <option value="">Select employee</option>

                          {Object.entries(employeesMap)
                            .sort(([, a], [, b]) => a.localeCompare(b))
                            .map(([id, name]) => (
                              <option key={id} value={id}>
                                {name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <small>Choose who you&apos;re updating.</small>
                    </label>

                    {/* Leave type */}
                    <label className="lm-balance-field">
                      <span>Leave Type</span>

                      <div className="lm-balance-control">
                        <Tags size={19} />

                        <select
                          value={lmTypeCode}
                          onChange={(e) => setLmTypeCode(e.target.value)}
                        >
                          <option value="">Select type</option>

                          {leaveTypesEnabled.map((t) => (
                            <option key={t.code} value={t.code}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <small>Pick the balance you want to change.</small>
                    </label>

                    {/* Action */}
                    <label className="lm-balance-field">
                      <span>Action</span>

                      <div className="lm-balance-control">
                        <Scale size={19} />

                        <select
                          value={lmMode}
                          onChange={(e) => setLmMode(e.target.value)}
                        >
                          <option value="add">Add hours</option>
                          <option value="subtract">Deduct hours</option>
                          <option value="set">Set exact balance</option>
                        </select>
                      </div>

                      <small>
                        Add gives more hours. Deduct removes hours. Set replaces
                        the current balance.
                      </small>
                    </label>

                    {/* Hours */}
                    <label className="lm-balance-field">
                      <span>Hours</span>

                      <div className="lm-balance-control">
                        <Clock3 size={19} />

                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={lmAmount}
                          onChange={(e) => setLmAmount(e.target.value)}
                        />
                      </div>

                      <small>Number of hours to apply.</small>
                    </label>
                  </div>

                  {/* Preview what will happen */}
                  {lmEmployeeId && lmTypeCode && Number(lmAmount) > 0 && (
                    <div className="lm-balance-action-preview">
                      <Info size={18} />

                      <span>
                        This will{" "}
                        <strong>
                          {lmMode === "add"
                            ? "add"
                            : lmMode === "subtract"
                            ? "deduct"
                            : "set"}
                        </strong>{" "}
                        <strong>{Number(lmAmount)} hours</strong>{" "}
                        {lmMode === "set" ? "as the exact balance for" : "for"}{" "}
                        <strong>
                          {employeesMap[lmEmployeeId] || "this employee"}
                        </strong>{" "}
                        under{" "}
                        <strong>
                          {leaveTypesEnabled.find((t) => t.code === lmTypeCode)
                            ?.label || lmTypeCode}
                        </strong>
                        .
                      </span>
                    </div>
                  )}

                  {/* Apply */}
                  <button
                    type="button"
                    className="lm-apply-balance-btn"
                    disabled={
                      !lmEmployeeId ||
                      !lmTypeCode ||
                      Number(lmAmount) <= 0 ||
                      leaveTypesEnabled.length === 0
                    }
                    onClick={grantHoursFromModal}
                  >
                    <Plus size={18} />

                    {lmMode === "add"
                      ? "Apply Update"
                      : lmMode === "subtract"
                      ? "Apply Deduction"
                      : "Set Balance"}
                  </button>

                  {/* Divider */}
                  <div className="lm-balance-divider" />

                  {/* Preview heading */}
                  <div className="lm-balance-preview-header">
                    <div className="lm-balance-preview-heading-left">
                      <div className="lm-preview-icon">
                        <Eye size={19} />
                      </div>

                      <div>
                        <h5>Preview Current Balances</h5>

                        <p>
                          View the selected employee&apos;s current time bank
                          balances.
                        </p>
                      </div>
                    </div>

                    {!lmEmployeeId && (
                      <span className="lm-select-employee-note">
                        Select an employee above
                      </span>
                    )}
                  </div>

                  {lmEmployeeId && (
                    <div className="lm-current-balances-wrap">
                      <EmployeeBalancesPreview
                        companyId={companyId}
                        employeeId={lmEmployeeId}
                        leaveTypes={leaveTypesAll}
                      />
                    </div>
                  )}
                </section>

                {/* Setup complete */}
                <div className="lm-balance-complete">
                  <div className="lm-balance-complete-left">
                    <div className="lm-complete-icon">
                      <Sparkles size={20} />
                    </div>

                    <div>
                      <strong>You&apos;ve completed the leave setup</strong>

                      <span>
                        Employees can now use the configured leave types and
                        balances when requesting time off.
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="lm-finish-setup-btn"
                    onClick={() => setShowLeaveSettings(false)}
                  >
                    <CheckCircle2 size={18} />
                    Finish Setup
                  </button>
                </div>
              </div>
            ) : lmTab === "types" ? (
              <div className="lm-body">
                <div className="lm-info-banner">
                  <Info size={21} />

                  <p>
                    Employees will select from these leave types when submitting
                    a leave request.
                  </p>
                </div>

                <LeaveTypesEditor
                  companyId={companyId}
                  leaveTypesAll={leaveTypesAll}
                />

                {leaveTypesEnabled.length > 0 && (
                  <div className="lm-next-step">
                    <div className="lm-next-step-left">
                      <div className="lm-next-icon">
                        <Sparkles size={20} />
                      </div>

                      <div>
                        <strong>Next step:</strong>{" "}
                        <span>Set your full-day hours in</span>{" "}
                        <button
                          type="button"
                          onClick={() => setLmTab("policy")}
                        >
                          Policy
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="lm-continue-btn"
                      onClick={() => setLmTab("policy")}
                    >
                      Save & Continue
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="lm-body">
                {/* Policy information */}
                <div className="lm-policy-info-banner">
                  <Info size={22} />

                  <div>
                    <p>
                      Set how many hours count as a <strong>Full Day</strong>{" "}
                      for your company. Full Day requests deduct this amount per
                      day.
                    </p>

                    <p>
                      Half Day requests use the employee&apos;s selected time
                      range.
                    </p>
                  </div>
                </div>

                {/* Main Policy Card */}
                <section className="lm-policy-card">
                  <div className="lm-policy-main">
                    {/* Full Day Hours */}
                    <div className="lm-policy-field-section">
                      <div className="lm-section-heading">
                        <h4>Full-day hours</h4>
                        <p>
                          Set the standard number of working hours that count as
                          one full day.
                        </p>
                      </div>

                      <label className="lm-policy-hours-field">
                        <Clock3 size={20} />

                        <input
                          type="number"
                          min={0.5}
                          max={24}
                          step={0.5}
                          value={fullDayHours}
                          onChange={(e) => setFullDayHours(e.target.value)}
                        />

                        <span className="lm-hours-suffix">hours</span>
                      </label>

                      <div className="lm-policy-field-help">
                        Enter a value between 0.5 and 24 hours.
                      </div>
                    </div>

                    {/* Live Example */}
                    <div className="lm-policy-example">
                      <div className="lm-policy-example-title">
                        <CheckCircle2 size={20} />
                        <strong>How this works</strong>
                      </div>

                      <div className="lm-policy-example-content">
                        <div className="lm-example-row">
                          <span>1 Full Day</span>

                          <strong>{Number(fullDayHours || 0)} hours</strong>
                        </div>

                        <div className="lm-example-divider" />

                        <div className="lm-example-row">
                          <span>2 Full Days</span>

                          <strong>{Number(fullDayHours || 0) * 2} hours</strong>
                        </div>

                        <p className="lm-example-note">
                          A 2-day Full Day request will deduct{" "}
                          <strong>{Number(fullDayHours || 0) * 2} hours</strong>{" "}
                          from the employee&apos;s balance.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Bottom / Next Step */}
                <div className="lm-next-step lm-policy-next-step">
                  <div className="lm-next-step-left">
                    <div className="lm-next-icon">
                      <Sparkles size={20} />
                    </div>

                    <div>
                      <strong>Next step:</strong>{" "}
                      <span>Assign employee balances in</span>{" "}
                      <button
                        type="button"
                        onClick={() => setLmTab("balances")}
                      >
                        Balances
                      </button>
                    </div>
                  </div>

                  <div className="lm-policy-footer-actions">
                    <button
                      type="button"
                      className="lm-cancel-btn"
                      onClick={() => setShowLeaveSettings(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="lm-save-policy-btn"
                      onClick={saveLeavePolicy}
                      disabled={savingPolicy}
                    >
                      <Save size={18} />

                      {savingPolicy ? "Saving..." : "Save Policy"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ Standard confirm modal (replaces window.confirm) */}
      {showConfirmModal && (
        <div
          className="confirm-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeConfirm();
          }}
        >
          <div className="confirm-modal-content">
            <div className="confirm-modal-header">
              <h3>{confirmTitle}</h3>
              <button className="confirm-modal-close" onClick={closeConfirm}>
                ✖
              </button>
            </div>

            <p className="confirm-modal-message">{confirmMessage}</p>

            <div className="confirm-modal-actions">
              <button className="confirm-btn cancel" onClick={closeConfirm}>
                Cancel
              </button>

              <button
                className={`confirm-btn ${
                  confirmDanger ? "danger" : "primary"
                }`}
                onClick={async () => {
                  try {
                    if (typeof onConfirm === "function") await onConfirm();
                  } finally {
                    closeConfirm();
                  }
                }}
              >
                {confirmCta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
